import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { RoomJob } from "../../../../models/sales";
import getAllMaterialsFromRoom from "../../../../util/getAllMaterialsFromRoom";
import { EmptySection } from "../../EmptySection";
import { MaterialBox } from "../../MaterialBox";
import useStyles from "./styles";

type MaterialCount = {
  name: string;
  quantity: number;
  price?: number;
};

type UniqueDestination = {
  type: "destination" | "location" | "modeDestination";
  name?: string;
  materials?: MaterialCount[];
  color?: string;
};

interface Props {
  roomJobList: RoomJob[];
  onCustomerView?: boolean;
}

const SummaryList = ({ roomJobList, onCustomerView = false }: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const uniqueDestinations = useMemo(() => {
    const destinations: UniqueDestination[] = [];

    if (roomJobList && !!roomJobList.length) {
      roomJobList.forEach((room) => {
        const allMaterials = getAllMaterialsFromRoom(room);

        allMaterials?.forEach((material) => {
          if (material.destinationId && material.destination) {
            destinations.push({
              type: "destination",
              name: material.destination?.name,
              color: material.destination?.color,
            });
          }
          if (material.destinationResidenceId && material.destinationResidence) {
            destinations.push({
              type: "location",
              name: material.destinationResidence.longAddress,
              color: material.destination?.color,
            });
          }

          if (material?.modeDestination && material?.modeDestinationId) {
            destinations.push({
              type: "modeDestination",
              name: material.modeDestination.name,
              color: material.destination?.color,
            });
          }
        });
      });
    }

    return Array.from(
      new Map(destinations.map((destination) => [destination.name, destination])).values()
    );
  }, [roomJobList]);

  useMemo(() => {
    if (uniqueDestinations && !!uniqueDestinations.length) {
      roomJobList.forEach((room) => {
        const allMaterials = getAllMaterialsFromRoom(room);

        allMaterials?.forEach((material) => {
          let destinationName: string = "";
          const materialName = material.serviceBooking.name;

          if (material.destination) destinationName = material.destination?.name;
          if (material.destinationResidence)
            destinationName = material.destinationResidence.longAddress || "";
          if (material?.modeDestination) destinationName = material.modeDestination?.name;

          uniqueDestinations.forEach((destination) => {
            if (destination.name === destinationName) {
              if (destination.materials) {
                const materialIndex = destination.materials.findIndex(
                  (material) => material.name === materialName
                );

                if (materialIndex !== -1) {
                  destination.materials[materialIndex].quantity += Number(material.quantity);
                } else {
                  destination.materials.push({
                    name: materialName,
                    quantity: Number(material.quantity),
                    price: material.price,
                  });
                }
              } else {
                destination.materials = [
                  {
                    name: materialName,
                    quantity: Number(material.quantity),
                    price: material.price,
                  },
                ];
              }
            }
          });
        });
      });
    }
  }, [uniqueDestinations]);

  const renderSummary = (destination: UniqueDestination) => {
    return (
      <div>
        <div className={classes.summaryHeader} data-cy="material-summary-header">
          {destination.type === "location" ? (
            <>
              <span className={classes.summaryTitle}>{translate("residence")}</span>
              <span className={classes.summaryAddress}>{destination.name}</span>
            </>
          ) : (
            <span className={classes.summaryTitle}>{destination.name}</span>
          )}
        </div>
        <div>
          {destination.materials &&
            destination.materials.map((material, index: number) => (
              <div key={`material-${index}`} className={classes.summaryList}>
                <span>{material.name}</span>
                <span>{material.quantity}</span>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const renderSummaryAsCustomerView = (destination: UniqueDestination) => {
    const title =
      destination.type === "modeDestination" || destination.type === "destination"
        ? destination.name
        : translate("residence");

    return (
      <MaterialBox
        title={title ?? ""}
        titleColor={destination.color}
        materials={destination.materials ?? []}
        destination={destination.name ?? ""}
      />
    );
  };

  return (
    <div className="p-2" data-cy="material-summary-list">
      {onCustomerView ? null : <span className={classes.summaryTitle}>{translate("summary")}</span>}
      <Grid
        className={classes.marginTop}
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}
      >
        {onCustomerView && !uniqueDestinations.length && <EmptySection />}
        {uniqueDestinations.map((destination, index: number) => (
          <Grid
            item
            xs={12}
            md={4}
            key={`destination-${index}`}
            data-cy={`material-summary-item-${destination.name}`}
          >
            {onCustomerView ? renderSummaryAsCustomerView(destination) : renderSummary(destination)}
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default SummaryList;
